import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { sedrErrorMessageKey } from './sedrErrors';

export function showSedrApplyFolderSetDialog(options: {
  roomId: string;
  onDone: () => void | Promise<void>;
}): void {
  const shell = createModalShell({
    titleKey: 'sedr.evidence.applyFolderSetTitle',
    titleId: 'sedr-apply-folder-set-title',
    helpTopic: dialogHelpTopic('sedrApplyFolderSet'),
  });

  shell.body.innerHTML = `
    <p class="muted" data-i18n="sedr.evidence.applyFolderSetHint"></p>
    <form class="form-grid" id="sedr-apply-folder-set-form">
      <label>
        <span data-i18n="sedr.evidence.applyFolderSetLabel"></span>
        <select name="starterSetId" required>
          <option value="" data-i18n="sedr.create.starterSetNone"></option>
        </select>
      </label>
      <p class="error-text" id="sedr-apply-folder-set-error" hidden></p>
    </form>
  `;

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'button ghost';
  cancelButton.setAttribute('data-i18n', 'common.cancel');

  const submitButton = document.createElement('button');
  submitButton.type = 'submit';
  submitButton.className = 'button primary';
  submitButton.setAttribute('form', 'sedr-apply-folder-set-form');
  submitButton.setAttribute('data-i18n', 'sedr.evidence.applyFolderSet');

  shell.actions.append(cancelButton, submitButton);
  document.body.append(shell.backdrop);
  applyI18n(shell.dialog);

  const form = shell.body.querySelector('#sedr-apply-folder-set-form') as HTMLFormElement;
  const errorEl = shell.body.querySelector('#sedr-apply-folder-set-error') as HTMLParagraphElement;
  const select = form.querySelector('select[name="starterSetId"]') as HTMLSelectElement;
  let submitting = false;
  let lastErrorKey = 'status.error';

  const refreshLocale = () => {
    if (!errorEl.hidden) {
      errorEl.textContent = t(lastErrorKey);
    }
    applyI18n(shell.dialog);
  };
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    if (submitting) return;
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  cancelButton.addEventListener('click', close);

  void (async () => {
    try {
      const sets = await sedrApi.listStarterSets('Folders');
      for (const set of sets) {
        const option = document.createElement('option');
        option.value = set.id;
        option.textContent = `${set.name} (${set.itemCount})`;
        select.append(option);
      }
      applyI18n(select);
    } catch (cause) {
      lastErrorKey = sedrErrorMessageKey(cause);
      errorEl.hidden = false;
      errorEl.textContent = t(lastErrorKey);
    }
  })();

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    if (submitting) return;
    const starterSetId = select.value.trim();
    if (!starterSetId) return;

    void (async () => {
      submitting = true;
      errorEl.hidden = true;
      submitButton.disabled = true;
      try {
        await sedrApi.applyFolderStarterSet(options.roomId, starterSetId);
        submitting = false;
        unbindLocale();
        unbindDismiss();
        shell.backdrop.remove();
        await options.onDone();
      } catch (cause) {
        submitting = false;
        submitButton.disabled = false;
        lastErrorKey = sedrErrorMessageKey(cause);
        errorEl.hidden = false;
        errorEl.textContent = t(lastErrorKey);
      }
    })();
  });
}
